// Client-side logo handling for Business Settings.
// Accepts image files only, resizes raster images down to a small square-ish
// data-URI, and enforces a ~200 KB ceiling so we never bloat the settings row.
// (Data-URI keeps the app self-contained/deploy-safe; Supabase Storage is a clean
//  future upgrade.)

const MAX_DIM = 320;                 // longest edge, px
const MAX_BYTES = 200 * 1024;        // 200 KB ceiling on the encoded string
const DATAURI_OVERHEAD = 1.4;        // base64 is ~1.37x the raw bytes

/**
 * Read + validate + resize an image File into a data-URI string.
 * @param {File} file
 * @returns {Promise<string>} data-URI
 * @throws {Error} on non-images or files that stay over the ceiling.
 */
export function resizeImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No file selected."));
    if (!file.type || !file.type.startsWith("image/")) return reject(new Error("Please choose an image file (PNG, JPG, SVG or WebP)."));

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.onload = () => {
      const src = String(reader.result || "");
      // SVG is vector — keep as-is if it's within the ceiling (can't raster-resize meaningfully).
      if (file.type === "image/svg+xml") {
        if (src.length > MAX_BYTES * DATAURI_OVERHEAD) return reject(new Error("Logo is too large (max ~200 KB)."));
        return resolve(src);
      }
      const img = new Image();
      img.onerror = () => reject(new Error("Could not read that image."));
      img.onload = () => {
        const scale = Math.min(1, MAX_DIM / Math.max(img.width || 1, img.height || 1));
        const w = Math.max(1, Math.round((img.width || 1) * scale));
        const h = Math.max(1, Math.round((img.height || 1) * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(src); // no canvas (very old env) — fall back to original
        ctx.drawImage(img, 0, 0, w, h);
        let out = canvas.toDataURL("image/png");
        if (out.length > MAX_BYTES * DATAURI_OVERHEAD) out = canvas.toDataURL("image/jpeg", 0.85);
        if (out.length > MAX_BYTES * DATAURI_OVERHEAD) return reject(new Error("That image is too large even after resizing (max ~200 KB). Try a simpler logo."));
        resolve(out);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}
